// src/pages/admin/AdminNotifications.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Tab,
  Tabs,
  Badge,
  useTheme,
  alpha,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  PersonAdd as PersonAddIcon,
  Article as ArticleIcon,
  ShoppingCart as MarketplaceIcon,
  Reviews as TestimonialIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  DeleteSweep as ClearAllIcon,
  CheckCircle as CheckIcon,
  FiberManualRecord as DotIcon
} from '@mui/icons-material';
import * as notificationApi from '../../services/notification';
import type { Notification } from '../../services/notification';
import { useNavigate } from 'react-router-dom';

const AdminNotifications: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationApi.getMyNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setSnackbar({ open: true, message: 'Failed to load notifications', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setSnackbar({ open: true, message: 'Marked as read', severity: 'success' });
    } catch (error) {
      console.error('Failed to mark as read:', error);
      setSnackbar({ open: true, message: 'Failed to mark as read', severity: 'error' });
    }
  };

  const handleDelete = async (notificationId: string) => {
    try {
      await notificationApi.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setSnackbar({ open: true, message: 'Notification deleted', severity: 'success' });
    } catch (error) {
      console.error('Failed to delete notification:', error);
      setSnackbar({ open: true, message: 'Failed to delete notification', severity: 'error' });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setSnackbar({ open: true, message: 'All notifications marked as read', severity: 'success' });
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      setSnackbar({ open: true, message: 'Failed to mark all as read', severity: 'error' });
    }
  };

  const handleClearAll = async () => {
    try {
      // Delete all notifications
      await Promise.all(notifications.map(n => notificationApi.deleteNotification(n.id)));
      setNotifications([]);
      setClearAllDialogOpen(false);
      setSnackbar({ open: true, message: 'All notifications cleared', severity: 'success' });
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
      setSnackbar({ open: true, message: 'Failed to clear notifications', severity: 'error' });
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read and navigate if there's a link
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getNotificationIcon = (type: string) => {
    if (type.includes('member') || type.includes('connection')) return <PersonAddIcon />;
    if (type.includes('article')) return <ArticleIcon />;
    if (type.includes('marketplace') || type.includes('investor')) return <MarketplaceIcon />;
    if (type.includes('testimonial')) return <TestimonialIcon />;
    return <NotificationsIcon />;
  };

  const getNotificationColor = (type: string) => {
    if (type.includes('member') || type.includes('connection')) return theme.palette.info.main;
    if (type.includes('article')) return theme.palette.primary.main;
    if (type.includes('marketplace') || type.includes('investor')) return theme.palette.success.main;
    if (type.includes('testimonial')) return theme.palette.warning.main;
    return theme.palette.secondary.main;
  };

  const filteredNotifications = notifications.filter(notif => {
    if (tabValue === 0) return true; // All
    if (tabValue === 1) return !notif.isRead; // Unread
    if (tabValue === 2) return notif.isRead; // Read
    return true;
  });

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <NotificationsIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight={600}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} Unread`}
              color="error"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {unreadCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CheckIcon />}
              onClick={handleMarkAllAsRead}
              sx={{ textTransform: 'none' }}
            >
              Mark All Read
            </Button>
          )}
          {notifications.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<ClearAllIcon />}
              onClick={() => setClearAllDialogOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Clear All
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={
              <Badge badgeContent={notifications.length} color="primary" max={999}>
                All
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={unreadCount} color="error" max={999}>
                Unread
              </Badge>
            }
          />
          <Tab label="Read" />
        </Tabs>
      </Paper>

      {/* Notifications List */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {filteredNotifications.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary={
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        {tabValue === 1 ? 'No unread notifications' : tabValue === 2 ? 'No read notifications' : 'No notifications'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tabValue === 1 ? "You're all caught up!" : tabValue === 2 ? 'Read notifications will appear here' : 'Notifications will appear here'}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ) : (
              filteredNotifications.map((notif, index) => (
                <React.Fragment key={notif.id}>
                  <ListItem
                    sx={{
                      bgcolor: !notif.isRead ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                      cursor: notif.link ? 'pointer' : 'default',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08)
                      },
                      py: 2
                    }}
                    onClick={() => notif.link && handleNotificationClick(notif)}
                    secondaryAction={
                      <Box>
                        {!notif.isRead && (
                          <IconButton
                            edge="end"
                            size="small"
                            sx={{ mr: 1 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(notif.id);
                            }}
                            title="Mark as read"
                          >
                            <MarkReadIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(notif.id);
                          }}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: getNotificationColor(notif.type) }}>
                        {getNotificationIcon(notif.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight={!notif.isRead ? 600 : 400}>
                            {notif.title}
                          </Typography>
                          {!notif.isRead && (
                            <DotIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography variant="body2" color="text.secondary" component="div">
                            {notif.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {new Date(notif.createdAt).toLocaleString()}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                  {index < filteredNotifications.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </List>
        )}
      </Paper>

      {/* Clear All Confirmation Dialog */}
      <Dialog open={clearAllDialogOpen} onClose={() => setClearAllDialogOpen(false)}>
        <DialogTitle>Clear All Notifications?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all {notifications.length} notifications. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleClearAll} color="error" variant="contained">
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminNotifications;
